import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { FilterFieldOption } from '~/components/ui';
import type { FilterValueInputRenderer } from '~/components/ui/FilterRow';
import { FilterRowsPanel } from '~/components/ui/FilterRowsPanel';
import { multiSelectValueInput } from '~/components/ui/multiSelectValueInput';
import {
  buyListStatusOptions,
  newBuyListFilterRow,
} from '~/pages/buy-lists/lib/buyListFilterFields';
import {
  BUY_LIST_FILTER_FIELD,
  isListField,
  type BuyListFilterRow,
} from '~/pages/buy-lists/lib/buyListFilters';

type BuyListFilterPanelProps = {
  readonly rows: BuyListFilterRow[];
  readonly fieldOptions: FilterFieldOption[];
  readonly productOptions: { label: string; value: string }[];
  readonly isLoadingProducts: boolean;
  readonly maxHeight?: number;
  readonly bottomContent?: ReactNode;
  readonly onRowsChange: (rows: BuyListFilterRow[]) => void;
};

function initialValueFor(field: string): string | string[] {
  return isListField(field) ? [] : '';
}

export function BuyListFilterPanel({
  rows,
  fieldOptions,
  productOptions,
  isLoadingProducts,
  maxHeight,
  bottomContent,
  onRowsChange,
}: BuyListFilterPanelProps) {
  const { t } = useTranslation('inventory');

  const valueInputs: Partial<Record<string, FilterValueInputRenderer>> = {
    [BUY_LIST_FILTER_FIELD.platformProductIds]: multiSelectValueInput({
      options: productOptions,
      isLoading: isLoadingProducts,
      placeholder: t('inventory.buy_list.filter.product_placeholder'),
      ariaLabel: t('inventory.buy_list.filter.field_product'),
      testIdPrefix: 'buy-list-filter-products',
    }),
    [BUY_LIST_FILTER_FIELD.status]: multiSelectValueInput({
      options: buyListStatusOptions(t),
      isLoading: false,
      placeholder: t('inventory.buy_list.filter.status_placeholder'),
      ariaLabel: t('inventory.buy_list.filter.field_status'),
      testIdPrefix: 'buy-list-filter-statuses',
    }),
  };

  return (
    <FilterRowsPanel
      namespace="inventory"
      keyPrefix="inventory.buy_list.filter"
      rows={rows}
      fieldOptions={fieldOptions}
      newRow={newBuyListFilterRow}
      initialValueFor={initialValueFor}
      valueInputFor={(field) => valueInputs[field]}
      maxHeight={maxHeight}
      bottomContent={bottomContent}
      onRowsChange={onRowsChange}
    />
  );
}
